/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import adminApi from '../../services/adminApi';
import AdminLayout from '../../components/AdminLayout';
import './SubmitJob.css';

const BASE_MODELS = [
  {
    id: 'mistral-7b',
    label: 'Mistral 7B Instruct',
    model: 'mistralai/Mistral-7B-Instruct-v0.2',
    taskType: 'conversational',
    description: 'Best for Q&A and domain knowledge',
    useCases: 'WMS, iSeries, Civil, Stitching',
    badge: 'Recommended'
  },
  {
    id: 'codellama-7b',
    label: 'CodeLlama 7B',
    model: 'codellama/CodeLlama-7b-hf',
    taskType: 'code_generation',
    description: 'Best for Cypher, SQL, code generation',
    useCases: 'SkillCode, Cypher FM',
    badge: 'New'
  },
  {
    id: 'codellama-2b',
    label: 'CodeLlama 2B',
    model: 'codellama/CodeLlama-2b-hf',
    taskType: 'code_generation',
    description: 'Lightweight and fast code generation',
    useCases: 'Quick Cypher tasks',
    badge: 'Fast'
  }
];

const SubmitJob = () => {
  const navigate = useNavigate();
  const { user } = useAdminAuth();

  // Form state
  const [formData, setFormData] = useState({
    domain: '',
    description: '',
    datasetSource: 'upload',  // 'upload' | 'multiple' | 'library' | 'kaggle'
    trainingParams: {
      epochs: 3,
      learningRate: 0.0001,
      batchSize: 4,
      maxSequenceLength: 512
    },
    kaggleConfig: {
      notebookPath: '',
      datasetId: ''
    }
  });

  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [preparedData, setPreparedData] = useState(null);
  const [removeDuplicates, setRemoveDuplicates] = useState(true);

  // NEW: Library datasets state
  const [libraryDatasets, setLibraryDatasets] = useState([]);
  const [selectedDatasetIds, setSelectedDatasetIds] = useState([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [storeUploaded, setStoreUploaded] = useState(true); // NEW: Option to store uploaded files

  // NEW: Training config state
  const [trainingConfigs, setTrainingConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loadingConfigs, setLoadingConfigs] = useState(false);

  // Base model selection state
  const [selectedBaseModel, setSelectedBaseModel] = useState({
    id: 'mistral-7b',
    model: 'mistralai/Mistral-7B-Instruct-v0.2',
    taskType: 'conversational',
    label: 'Mistral 7B Instruct'
  });

  /**
   * NEW: Load datasets from library when domain changes or source is library
   */
  useEffect(() => {
    if (formData.datasetSource === 'library' && formData.domain) {
      loadLibraryDatasets(formData.domain);
    }
  }, [formData.domain, formData.datasetSource]);

  /**
   * NEW: Load training configs when domain changes
   */
  useEffect(() => {
    if (formData.domain) {
      loadTrainingConfigs(formData.domain);
    }
  }, [formData.domain]);

  /**
   * NEW: Fetch training configs from API
   */
  const loadTrainingConfigs = async (domain) => {
    try {
      setLoadingConfigs(true);
      const data = await adminApi.getTrainingConfigs(domain);

      if (data.success && data.configs) {
        setTrainingConfigs(data.configs);
        // Auto-select active config
        const activeConfig = data.configs.find(c => c.isActive) || data.configs[0];
        setSelectedConfig(activeConfig);

        // Update form with config values
        if (activeConfig) {
          setFormData(prev => ({
            ...prev,
            trainingParams: {
              epochs: activeConfig.training?.epochs || 5,
              learningRate: activeConfig.training?.learningRate || 0.0002,
              batchSize: activeConfig.training?.batchSize || 2,
              maxSequenceLength: activeConfig.training?.maxSeqLength || 1024
            }
          }));
        }
      }
    } catch (err) {
      console.error('Error loading training configs:', err);
      // Use defaults if API fails
      setTrainingConfigs([]);
      setSelectedConfig(null);
    } finally {
      setLoadingConfigs(false);
    }
  };

  /**
   * NEW: Fetch datasets from MongoDB
   */
  const loadLibraryDatasets = async (domain) => {
    try {
      setLoadingDatasets(true);
      const response = await adminApi.getTrainingDatasets({ domain, status: 'ready' });
      setLibraryDatasets(response.datasets || []);
      // Auto-select all datasets by default
      setSelectedDatasetIds((response.datasets || []).map(d => d._id));
    } catch (err) {
      console.error('Error loading datasets:', err);
      setLibraryDatasets([]);
    } finally {
      setLoadingDatasets(false);
    }
  };

  /**
   * NEW: Toggle dataset selection
   */
  const toggleDatasetSelection = (datasetId) => {
    setSelectedDatasetIds(prev => 
      prev.includes(datasetId)
        ? prev.filter(id => id !== datasetId)
        : [...prev, datasetId]
    );
  };

  /**
   * NEW: Select/deselect all datasets
   */
  const toggleSelectAll = () => {
    if (selectedDatasetIds.length === libraryDatasets.length) {
      setSelectedDatasetIds([]);
    } else {
      setSelectedDatasetIds(libraryDatasets.map(d => d._id));
    }
  };

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('trainingParams.')) {
      const param = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        trainingParams: {
          ...prev.trainingParams,
          [param]: parseFloat(value) || value
        }
      }));
    } else if (name.startsWith('kaggleConfig.')) {
      const param = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        kaggleConfig: {
          ...prev.kaggleConfig,
          [param]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // Reset selections when domain changes
      if (name === 'domain') {
        setSelectedDatasetIds([]);
        setLibraryDatasets([]);
      }
    }
  };

  /**
   * Handle file selection
   */
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (selectedFile) {
      // Validate file type
      const validTypes = ['.csv', '.json', '.jsonl', '.txt'];
      const fileExt = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

      if (!validTypes.includes(fileExt)) {
        setError(`Invalid file type. Allowed: ${validTypes.join(', ')}`);
        return;
      }

      // Validate file size (max 500MB)
      if (selectedFile.size > 500 * 1024 * 1024) {
        setError('File size must be less than 500MB');
        return;
      }

      setFile(selectedFile);
      setError('');
    }
  };

  /**
   * Handle multiple files selection
   */
  const handleMultipleFilesChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (selectedFiles.length === 0) return;

    // Validate each file
    const validTypes = ['.json', '.jsonl'];
    const invalidFiles = [];
    const oversizedFiles = [];

    for (const file of selectedFiles) {
      const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validTypes.includes(fileExt)) {
        invalidFiles.push(file.name);
      }

      if (file.size > 500 * 1024 * 1024) {
        oversizedFiles.push(file.name);
      }
    }

    if (invalidFiles.length > 0) {
      setError(`Invalid file type(s): ${invalidFiles.join(', ')}. Only JSON/JSONL allowed for multi-file merge.`);
      return;
    }

    if (oversizedFiles.length > 0) {
      setError(`File(s) too large: ${oversizedFiles.join(', ')}. Max 500MB per file.`);
      return;
    }

    setFiles(selectedFiles);
    setPreparedData(null); // Reset prepared data when new files selected
    setError('');
  };

  /**
   * Remove a file from multi-file list
   */
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreparedData(null);
  };

  /**
   * Prepare data - combine multiple files
   */
  const handlePrepareData = async () => {
    if (files.length === 0) {
      setError('Please select files to combine');
      return;
    }

    try {
      setPreparing(true);
      setError('');

      const result = await adminApi.prepareData(files, removeDuplicates, (progress) => {
        setUploadProgress(progress);
      });

      setPreparedData(result.data);
      setUploadProgress(0);
      setError('');

    } catch (err) {
      console.error('Prepare data error:', err);
      setError(err.response?.data?.error || 'Failed to prepare data');
      setPreparedData(null);
    } finally {
      setPreparing(false);
    }
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    if (!formData.domain) {
      setError('Please select a domain');
      return false;
    }

    if (!formData.description) {
      setError('Please provide a description');
      return false;
    }

    if (formData.datasetSource === 'upload' && !file) {
      setError('Please upload a dataset file');
      return false;
    }

    if (formData.datasetSource === 'multiple' && !preparedData) {
      setError('Please prepare data by combining files first');
      return false;
    }

    // NEW: Validate library selection
    if (formData.datasetSource === 'library' && selectedDatasetIds.length === 0) {
      setError('Please select at least one dataset from the library');
      return false;
    }

    if (formData.datasetSource === 'kaggle') {
      if (!formData.kaggleConfig.notebookPath) {
        setError('Please provide Kaggle notebook path');
        return false;
      }
      if (!formData.kaggleConfig.datasetId) {
        setError('Please provide Kaggle dataset ID');
        return false;
      }
    }

    return true;
  };

  /**
   * Handle form submit
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess(false);

      let datasetPath = '';
      let datasetSize = null;
      let datasetIds = null;

      // Upload file if dataset source is upload
      if (formData.datasetSource === 'upload' && file) {
        const uploadResponse = await adminApi.uploadTrainingData(file, (progress) => {
          setUploadProgress(progress);
        });

        datasetPath = uploadResponse.data.filePath;

        // NEW: Optionally store the uploaded file to MongoDB for future use
        if (storeUploaded) {
          try {
            await adminApi.storeTrainingDataset({
              file,
              domain: formData.domain,
              name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
              description: formData.description
            });
            console.log('📚 Training data stored to library for future use');
          } catch (storeErr) {
            console.warn('Could not store to library:', storeErr.message);
            // Continue with job submission anyway
          }
        }
      }

      // Use prepared data if dataset source is multiple
      if (formData.datasetSource === 'multiple' && preparedData) {
        datasetPath = preparedData.filePath;
      }

      // NEW: Use library datasets
      if (formData.datasetSource === 'library') {
        datasetIds = selectedDatasetIds;
        // The backend will read from MongoDB and create the combined file
      }

      // Submit job - USE SELECTED CONFIG VALUES
      const response = await adminApi.submitTrainingJob({
        domain: formData.domain,
        description: formData.description,
        trainingDataPath: datasetPath,
        datasetPath,
        datasetSize,
        datasetIds,  // NEW: Pass selected dataset IDs
        datasetSource: formData.datasetSource,  // NEW: Tell backend the source type
        baseModel: formData.baseModel || selectedBaseModel.model,
        taskType: formData.taskType || selectedBaseModel.taskType,
        parameters: {
          ...formData.trainingParams,
          // Use selected config values (or defaults from balanced preset)
          loraR: selectedConfig?.lora?.r || 64,
          loraAlpha: selectedConfig?.lora?.alpha || 128,
          loraDropout: selectedConfig?.lora?.dropout || 0.05,
          targetModules: selectedConfig?.lora?.targetModules || [
            'q_proj', 'k_proj', 'v_proj', 'o_proj', 
            'gate_proj', 'up_proj', 'down_proj'
          ],
          gradientAccumulation: selectedConfig?.training?.gradientAccumulation || 8,
          configId: selectedConfig?._id,
          configName: selectedConfig?.name
        },
        kaggleConfig: formData.datasetSource === 'kaggle' ? formData.kaggleConfig : null
      });

      setSuccess(true);
      setUploadProgress(0);

      // Navigate to job detail page after a delay
      setTimeout(() => {
        navigate(`/admin/training/jobs/${response.data.jobId}`);
      }, 2000);

    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.error || 'Failed to submit training job');
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle form reset
   */
  const handleReset = () => {
    setFormData({
      domain: '',
      description: '',
      datasetSource: 'upload',
      trainingParams: {
        epochs: 3,
        learningRate: 0.0001,
        batchSize: 4,
        maxSequenceLength: 512
      },
      kaggleConfig: {
        notebookPath: '',
        datasetId: ''
      }
    });
    setSelectedBaseModel({
      id: 'mistral-7b',
      model: 'mistralai/Mistral-7B-Instruct-v0.2',
      taskType: 'conversational',
      label: 'Mistral 7B Instruct'
    });
    setFile(null);
    setFiles([]);
    setPreparedData(null);
    setSelectedDatasetIds([]);
    setLibraryDatasets([]);
    setTrainingConfigs([]);
    setSelectedConfig(null);
    setError('');
    setSuccess(false);
    setUploadProgress(0);
  };

  /**
   * Format file size
   */
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  /**
   * Calculate total items from selected datasets
   */
  const getSelectedItemsCount = () => {
    return libraryDatasets
      .filter(d => selectedDatasetIds.includes(d._id))
      .reduce((sum, d) => sum + (d.recordCount || 0), 0);
  };

  return (
    <div className="submit-job">
      {/* Page Header */}
      <div className="page-header">
        <button className="back-button" onClick={() => navigate('/admin/training/jobs')}>
          ← Back to Jobs
        </button>
        <h1>Submit Training Job</h1>
        <p className="page-subtitle">Fine-tune domain-specific LoRA adapter</p>
      </div>

      {/* Success Alert */}
      {success && (
        <div className="success-alert">
          <span className="success-icon">🎉</span>
          <div>
            <strong>Training Job Submitted!</strong>
            <p>Your job has been queued and will start processing shortly. Redirecting to job details...</p>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="error-alert">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Job Form */}
      <form className="job-form" onSubmit={handleSubmit}>
        {/* Domain Selection */}
        <div className="form-section">
          <h2>1. Domain Selection</h2>

          <div className="form-group">
            <label htmlFor="domain">
              Domain <span className="required">*</span>
            </label>
            <select
              id="domain"
              name="domain"
              value={formData.domain}
              onChange={handleChange}
              disabled={loading}
              required
            >
              <option value="">Select a domain</option>
              <option value="WMS">WMS (Warehouse Management System)</option>
              <option value="iSeries">iSeries/AS400</option>
              <option value="Civil">Civil Engineering</option>
              <option value="Stitching">Apparel/Stitching</option>
              <option value="ERPNext">ERPNext</option>
              <option value="Platform">Platform General</option>
              <option value="SkillCode">SkillCode (AI Software Engineering)</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="description">
              Description <span className="required">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={loading}
              placeholder="Describe what this adapter should learn and how it will be used"
              rows="4"
              required
            />
            <span className="help-text">
              Explain what this adapter should learn and how it will be used
            </span>
          </div>
        </div>

        {/* Base Model Selection */}
        <div className="form-section">
          <h2>2. Base Model</h2>
          <p className="section-description">
            Select the foundation model for training. Auto-sets LoRA parameters based on task type.
          </p>

          <div className="model-cards">
            {BASE_MODELS.map(m => (
              <div
                key={m.id}
                className={`model-card ${selectedBaseModel.id === m.id ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedBaseModel(m);
                  setFormData(prev => ({
                    ...prev,
                    baseModel: m.model,
                    taskType: m.taskType
                  }));
                }}
              >
                <div className="model-card-header">
                  <h3>{m.label}</h3>
                  <span className={`badge badge-${m.id}`}>{m.badge}</span>
                </div>
                <p>{m.description}</p>
                <small>Use: {m.useCases}</small>
              </div>
            ))}
          </div>

          <div className="selected-model-info">
            <span>Selected: <strong>{selectedBaseModel.label}</strong></span>
            <span>Task: <strong>{selectedBaseModel.taskType}</strong></span>
          </div>
        </div>

        {/* Dataset Configuration */}
        <div className="form-section">
          <h2>3. Dataset Configuration</h2>

          <div className="form-group">
            <label>Dataset Source</label>
            <div className="radio-group">
              {/* NEW: Library Option - Recommended */}
              <label className={`radio-option ${formData.datasetSource === 'library' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="datasetSource"
                  value="library"
                  checked={formData.datasetSource === 'library'}
                  onChange={handleChange}
                  disabled={loading}
                />
                <div className="radio-content">
                  <strong>
                    Select from Library
                    <span className="recommended-badge">⭐ Recommended</span>
                  </strong>
                  <p>Choose from datasets already stored in the system</p>
                  <ul className="option-benefits">
                    <li>✓ Data persists for re-training</li>
                    <li>✓ Combine multiple datasets</li>
                    <li>✓ Version tracking</li>
                  </ul>
                </div>
              </label>

              <label className={`radio-option ${formData.datasetSource === 'upload' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="datasetSource"
                  value="upload"
                  checked={formData.datasetSource === 'upload'}
                  onChange={handleChange}
                  disabled={loading}
                />
                <div className="radio-content">
                  <strong>Upload File</strong>
                  <p>Upload single training data file</p>
                </div>
              </label>

              <label className={`radio-option ${formData.datasetSource === 'multiple' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="datasetSource"
                  value="multiple"
                  checked={formData.datasetSource === 'multiple'}
                  onChange={handleChange}
                  disabled={loading}
                />
                <div className="radio-content">
                  <strong>Multiple Files (Auto-Merge)</strong>
                  <p>Combine multiple JSON/JSONL files into one</p>
                </div>
              </label>

              <label className={`radio-option ${formData.datasetSource === 'kaggle' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="datasetSource"
                  value="kaggle"
                  checked={formData.datasetSource === 'kaggle'}
                  onChange={handleChange}
                  disabled={loading}
                />
                <div className="radio-content">
                  <strong>Kaggle Notebook</strong>
                  <p>Train using Kaggle notebook with GPU</p>
                </div>
              </label>
            </div>
          </div>

          {/* NEW: Library Dataset Selection */}
          {formData.datasetSource === 'library' && (
            <div className="library-selection">
              {!formData.domain ? (
                <div className="select-domain-first">
                  <p>👆 Please select a domain first to see available datasets</p>
                </div>
              ) : loadingDatasets ? (
                <div className="loading-datasets">
                  <span className="spinner"></span>
                  Loading datasets...
                </div>
              ) : libraryDatasets.length === 0 ? (
                <div className="no-datasets">
                  <p>📭 No datasets found for {formData.domain}</p>
                  <p className="help-text">
                    Upload datasets first using the "Upload File" option, or go to{' '}
                    <a href="/admin/datasets">Training Data</a> to manage datasets.
                  </p>
                </div>
              ) : (
                <>
                  <div className="datasets-header">
                    <strong>Available Datasets for {formData.domain}</strong>
                    <button
                      type="button"
                      className="select-all-btn"
                      onClick={toggleSelectAll}
                    >
                      {selectedDatasetIds.length === libraryDatasets.length
                        ? 'Deselect All'
                        : 'Select All'}
                    </button>
                  </div>

                  <div className="datasets-list">
                    {libraryDatasets.map(dataset => (
                      <div
                        key={dataset._id}
                        className={`dataset-item ${selectedDatasetIds.includes(dataset._id) ? 'selected' : ''}`}
                        onClick={() => toggleDatasetSelection(dataset._id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDatasetIds.includes(dataset._id)}
                          onChange={() => toggleDatasetSelection(dataset._id)}
                        />
                        <div className="dataset-info">
                          <span className="dataset-name">{dataset.name || dataset.fileName}</span>
                          <span className="dataset-meta">
                            {dataset.recordCount || 0} records • {formatFileSize(dataset.fileSize || 0)}
                            {dataset.tags?.length > 0 && ` • ${dataset.tags.join(', ')}`}
                          </span>
                        </div>
                        <div className="dataset-status">
                          {dataset.status === 'ready' ? (
                            <span className="status-ready">✓ Ready</span>
                          ) : (
                            <span className="status-processing">⏳ {dataset.status}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="selection-summary">
                    Selected: <strong>{selectedDatasetIds.length}</strong> datasets
                    (<strong>{getSelectedItemsCount().toLocaleString()}</strong> training records)
                  </div>
                </>
              )}
            </div>
          )}

          {/* Single File Upload */}
          {formData.datasetSource === 'upload' && (
            <>
              <div className="form-group">
                <label>Training Data File <span className="required">*</span></label>
                <div className="file-upload-area">
                  {!file ? (
                    <label className="file-upload-label">
                      <input
                        type="file"
                        accept=".csv,.json,.jsonl,.txt"
                        onChange={handleFileChange}
                        disabled={loading}
                        style={{ display: 'none' }}
                      />
                      <span className="upload-icon">📁</span>
                      <span className="upload-text">Click to upload or drag and drop</span>
                      <span className="upload-help">CSV, JSON, JSONL, or TXT (max 500MB)</span>
                    </label>
                  ) : (
                    <div className="file-selected">
                      <div className="file-info">
                        <span className="file-icon">📄</span>
                        <div className="file-details">
                          <span className="file-name">{file.name}</span>
                          <span className="file-size">{formatFileSize(file.size)}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="remove-file"
                        onClick={() => setFile(null)}
                        disabled={loading}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                
                {/* NEW: Option to store uploaded file */}
                <label className="checkbox-label" style={{ marginTop: '12px' }}>
                  <input
                    type="checkbox"
                    checked={storeUploaded}
                    onChange={(e) => setStoreUploaded(e.target.checked)}
                    disabled={loading}
                  />
                  <span>Save to library for future use</span>
                </label>
              </div>

              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">Uploading... {uploadProgress}%</span>
                </div>
              )}
            </>
          )}

          {/* Multiple Files Upload */}
          {formData.datasetSource === 'multiple' && (
            <>
              <div className="form-group">
                <label>Training Data Files <span className="required">*</span></label>
                <div className="file-upload-area">
                  <label className="file-upload-label">
                    <input
                      type="file"
                      accept=".json,.jsonl"
                      multiple
                      onChange={handleMultipleFilesChange}
                      disabled={loading || preparing}
                      style={{ display: 'none' }}
                    />
                    <span className="upload-icon">📁</span>
                    <span className="upload-text">Click to upload or drag and drop</span>
                    <span className="upload-help">JSON or JSONL files only (max 20 files)</span>
                  </label>
                </div>
              </div>

              {/* Selected Files List */}
              {files.length > 0 && (
                <div className="selected-files-list">
                  <div className="files-header">
                    <strong>Selected Files ({files.length})</strong>
                    <button
                      type="button"
                      className="clear-files"
                      onClick={() => {
                        setFiles([]);
                        setPreparedData(null);
                      }}
                      disabled={preparing}
                    >
                      Clear All
                    </button>
                  </div>
                  {files.map((f, index) => (
                    <div key={index} className="file-item">
                      <span className="file-icon">📄</span>
                      <div className="file-details">
                        <span className="file-name">{f.name}</span>
                        <span className="file-size">{formatFileSize(f.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="remove-file"
                        onClick={() => removeFile(index)}
                        disabled={preparing}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Options */}
              {files.length > 0 && !preparedData && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={removeDuplicates}
                      onChange={(e) => setRemoveDuplicates(e.target.checked)}
                      disabled={preparing}
                    />
                    <span>Remove duplicate entries</span>
                  </label>
                  <span className="help-text">
                    Automatically removes duplicate question-answer pairs based on content similarity
                  </span>
                </div>
              )}

              {/* Prepare Data Button */}
              {files.length > 0 && !preparedData && (
                <div className="form-group">
                  <button
                    type="button"
                    className="prepare-button"
                    onClick={handlePrepareData}
                    disabled={preparing}
                  >
                    {preparing ? (
                      <>
                        <span className="spinner"></span>
                        Processing Files...
                      </>
                    ) : (
                      <>🔄 Combine Files</>
                    )}
                  </button>
                </div>
              )}

              {/* Statistics Display */}
              {preparedData && (
                <div className="prepared-data-card">
                  <div className="card-header">
                    <span className="success-icon">✓</span>
                    <strong>Data Prepared Successfully</strong>
                  </div>

                  <div className="stats-grid">
                    <div className="stat-item">
                      <span className="stat-label">Files Processed</span>
                      <span className="stat-value">{preparedData.statistics.filesProcessed}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Total Records</span>
                      <span className="stat-value">{preparedData.statistics.totalRecords}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Valid Records</span>
                      <span className="stat-value">{preparedData.statistics.validRecords}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Invalid Records</span>
                      <span className="stat-value">{preparedData.statistics.invalidRecords}</span>
                    </div>
                    {removeDuplicates && (
                      <div className="stat-item">
                        <span className="stat-label">Duplicates Removed</span>
                        <span className="stat-value">{preparedData.statistics.duplicatesRemoved}</span>
                      </div>
                    )}
                    <div className="stat-item highlight">
                      <span className="stat-label">Final Count</span>
                      <span className="stat-value">{preparedData.statistics.finalCount}</span>
                    </div>
                  </div>

                  <div className="file-info-row">
                    <span className="file-icon">📄</span>
                    <div>
                      <div className="filename">{preparedData.filename}</div>
                      <div className="filesize">
                        {(preparedData.size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="recombine-button"
                    onClick={() => setPreparedData(null)}
                    disabled={loading}
                  >
                    ↻ Recombine with Different Settings
                  </button>
                </div>
              )}
            </>
          )}

          {/* Kaggle Option */}
          {formData.datasetSource === 'kaggle' && (
            <>
              <div className="form-group">
                <label htmlFor="kaggleNotebook">
                  Kaggle Notebook Path <span className="required">*</span>
                </label>
                <input
                  id="kaggleNotebook"
                  type="text"
                  name="kaggleConfig.notebookPath"
                  value={formData.kaggleConfig.notebookPath}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="username/notebook-name"
                  required
                />
                <span className="help-text">
                  Format: username/notebook-name (e.g., johndoe/wms-training)
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="kaggleDataset">
                  Kaggle Dataset ID <span className="required">*</span>
                </label>
                <input
                  id="kaggleDataset"
                  type="text"
                  name="kaggleConfig.datasetId"
                  value={formData.kaggleConfig.datasetId}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="username/dataset-name"
                  required
                />
                <span className="help-text">
                  Format: username/dataset-name (e.g., johndoe/wms-qa-pairs)
                </span>
              </div>
            </>
          )}
        </div>

        {/* NEW: Training Configuration Selection */}
        {formData.domain && (
          <div className="form-section">
            <h2>4. Training Configuration</h2>
            <p className="section-description">
              Select a pre-configured training profile. This affects LoRA parameters and training settings.
            </p>

            {loadingConfigs ? (
              <div className="loading-configs">
                <span className="spinner"></span>
                Loading configurations...
              </div>
            ) : trainingConfigs.length > 0 ? (
              <div className="config-cards">
                {trainingConfigs.map((config) => (
                  <div
                    key={config._id}
                    className={`config-card ${selectedConfig?._id === config._id ? 'selected' : ''} ${config.preset}`}
                    onClick={() => {
                      setSelectedConfig(config);
                      // Update form values from config
                      setFormData(prev => ({
                        ...prev,
                        trainingParams: {
                          epochs: config.training?.epochs || 5,
                          learningRate: config.training?.learningRate || 0.0002,
                          batchSize: config.training?.batchSize || 2,
                          maxSequenceLength: config.training?.maxSeqLength || 1024
                        }
                      }));
                    }}
                  >
                    <div className="config-header">
                      <span className="config-icon">
                        {config.preset === 'conservative' && '🛡️'}
                        {config.preset === 'balanced' && '⚖️'}
                        {config.preset === 'aggressive' && '🚀'}
                        {config.preset === 'experimental' && '🔬'}
                        {config.preset === 'custom' && '⚙️'}
                      </span>
                      <span className="config-name">{config.preset?.charAt(0).toUpperCase() + config.preset?.slice(1) || 'Custom'}</span>
                      {config.isActive && <span className="active-badge">Active</span>}
                    </div>
                    <div className="config-details">
                      <span>R={config.lora?.r}, α={config.lora?.alpha}</span>
                      <span>{config.training?.epochs} epochs</span>
                    </div>
                    {selectedConfig?._id === config._id && (
                      <div className="selected-indicator">✓</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-configs">
                <p>No configurations found for this domain. Using default settings (R=64, α=128).</p>
              </div>
            )}

            {/* Selected Config Details */}
            {selectedConfig && (
              <div className="selected-config-summary">
                <h4>Selected: {selectedConfig.name}</h4>
                <div className="config-summary-grid">
                  <div><strong>LoRA Rank:</strong> {selectedConfig.lora?.r}</div>
                  <div><strong>LoRA Alpha:</strong> {selectedConfig.lora?.alpha}</div>
                  <div><strong>Target Modules:</strong> {selectedConfig.lora?.targetModules?.length || 7} layers</div>
                  <div><strong>Effective Batch:</strong> {(selectedConfig.training?.batchSize || 2) * (selectedConfig.training?.gradientAccumulation || 8)}</div>
                </div>
                {selectedConfig.description && (
                  <p className="config-description">{selectedConfig.description}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Training Parameters */}
        <div className="form-section">
          <h2>5. Training Parameters</h2>
          <p className="section-description">
            Values are auto-populated from selected config. Adjust if needed.
          </p>

          <div className="params-grid">
            <div className="form-group">
              <label htmlFor="epochs">Epochs</label>
              <input
                id="epochs"
                type="number"
                name="trainingParams.epochs"
                value={formData.trainingParams.epochs}
                onChange={handleChange}
                disabled={loading}
                min="1"
                max="10"
              />
              <span className="help-text">Recommended: 3-5</span>
            </div>

            <div className="form-group">
              <label htmlFor="learningRate">Learning Rate</label>
              <input
                id="learningRate"
                type="number"
                name="trainingParams.learningRate"
                value={formData.trainingParams.learningRate}
                onChange={handleChange}
                disabled={loading}
                step="0.00001"
                min="0.00001"
                max="0.001"
              />
              <span className="help-text">Recommended: 0.0001</span>
            </div>

            <div className="form-group">
              <label htmlFor="batchSize">Batch Size</label>
              <input
                id="batchSize"
                type="number"
                name="trainingParams.batchSize"
                value={formData.trainingParams.batchSize}
                onChange={handleChange}
                disabled={loading}
                min="1"
                max="16"
              />
              <span className="help-text">Recommended: 4-8</span>
            </div>

            <div className="form-group">
              <label htmlFor="maxSequenceLength">Max Sequence Length</label>
              <input
                id="maxSequenceLength"
                type="number"
                name="trainingParams.maxSequenceLength"
                value={formData.trainingParams.maxSequenceLength}
                onChange={handleChange}
                disabled={loading}
                min="128"
                max="2048"
                step="128"
              />
              <span className="help-text">Recommended: 512</span>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="reset-button"
            onClick={handleReset}
            disabled={loading}
          >
            Reset Form
          </button>

          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Submitting...
              </>
            ) : (
              <>Submit Training Job</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SubmitJob;
